"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, ChevronRight, Clock, FileText, FlaskConical, Layers, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const knowledgeHubs = [{
  title: "Raw Material Identification",
  description: "Guidance on 100% incoming container verification, handheld Raman spectroscopy, and regulatory compliance protocols.",
  href: "/RawMaterialIdentificationKnowledgeCentrePage",
  icon: FlaskConical,
  tag: "Spectroscopy"
}, {
  title: "Powder Testing & Characterization",
  description: "Technical notes and pharmacopoeial methods for bulk density, tap density, shear testing, and flowability index.",
  href: "/PowderTestingKnowledgeCentrePage",
  icon: Layers,
  tag: "Physical Testing"
}, {
  title: "Process Analytical Technology (PAT)",
  description: "In-line blend uniformity monitoring, drying endpoint detection, and real-time release testing frameworks.",
  href: "/PatKnowledgeCentrePage",
  icon: Sparkles,
  tag: "Continuous Mfg"
}, {
  title: "Safety & Security Identification",
  description: "Field identification of unknown chemical threats, illicit narcotics, and hazardous compounds for regulated environments.",
  href: "/SafetySecurityKnowledgeCentrePage",
  icon: ShieldCheck,
  tag: "Field & Lab"
}];
function LatestArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
            <Skeleton className="h-44 w-full rounded-lg" />
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <div className="mt-auto flex items-center justify-between pt-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/40 p-8 text-center">
        <p className="text-sm text-muted-foreground">Unable to load technical articles at this moment.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4 gap-2">
          <RefreshCw className="size-4" />
          Retry loading
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card p-10 text-center">
        <BookOpen className="size-10 text-muted-foreground/60 mb-3" />
        <h3 className="font-default text-lg font-semibold text-foreground">No Articles Published Yet</h3>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          New technical application notes, whitepapers, and regulatory updates will appear here once published.
        </p>
        <Link to="/ContactPage" className="mt-4">
          <Button variant="outline" size="sm">
            Contact an Application Specialist
          </Button>
        </Link>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <article key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-200 hover:border-primary/40 hover:shadow-md">
          <div>
            {post.featuredImage ? <div className="aspect-video w-full overflow-hidden bg-muted">
                <img src={post.featuredImage} alt={post.title || "Technical Article"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div> : <div className="flex aspect-video w-full items-center justify-center bg-secondary/60">
                <FileText className="size-10 text-muted-foreground/40" />
              </div>}

            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {post.categories && post.categories.length > 0 && <Badge variant="secondary" className="font-normal text-[11px]">
                    {post.categories[0].name}
                  </Badge>}
                {post.date && <div className="flex items-center gap-1">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString("en-IE", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
                    </time>
                  </div>}
              </div>

              <h3 className="mt-3 font-default text-lg font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                {post.title}
              </h3>

              {post.excerpt && <div className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
            </div>
          </div>

          <div className="border-t border-border px-6 py-4 bg-muted/20 flex items-center justify-between">
            <span className="inline-flex items-center text-xs font-medium text-muted-foreground">
              Technical Note
            </span>
            {post.link ? <Link to={post.link} className="inline-flex items-center text-sm font-medium text-primary hover:underline gap-1">
                Read Article
                <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </Link> : <Link to="/ResourcesPage" className="inline-flex items-center text-sm font-medium text-primary hover:underline gap-1">
                View Details
                <ChevronRight className="size-4" />
              </Link>}
          </div>
        </article>)}
    </div>;
}
export default function Resources() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="resources" data-nav="light" className="w-full bg-background py-16 md:py-24">
      <div className="page-container">
        {/* Section Heading */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground mb-4">
              <BookOpen className="size-3.5 text-primary" />
              <span>Knowledge Hub & Guidance</span>
            </div>
            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Technical Resources & Regulatory Insights
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Explore verified methodologies, analytical guides, and compliance notes prepared for regulated laboratories and manufacturing teams across Ireland and the UK.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/ApplicationConsultationPage">
              <Button variant="outline" size="default">
                Request Consultation
              </Button>
            </Link>
            <Link to="/ResourcesPage">
              <Button variant="default" size="default" className="gap-2">
                All Resources
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Curated Knowledge Centres Section */}
        <div className="mt-12">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="font-default text-xl font-semibold text-foreground">
              Curated Knowledge Centres
            </h3>
            <span className="text-xs text-muted-foreground">Select a discipline</span>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Primary Featured Knowledge Card */}
            <div className="lg:col-span-5 flex">
              <Card className="flex flex-col justify-between border-2 border-primary/20 bg-linear-to-b from-secondary/50 to-card shadow-md transition-all hover:border-primary/40">
                <CardHeader>
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="default" className="gap-1.5 font-medium">
                      <Sparkles className="size-3" />
                      Featured Hub
                    </Badge>
                    <span className="text-xs font-medium text-muted-foreground">HPLC ReSolve</span>
                  </div>
                  <CardTitle className="mt-4 font-default text-2xl font-bold text-foreground">
                    HPLC Sustainability Knowledge Centre
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground leading-relaxed mt-2">
                    Comprehensive technical data on automated solvent recovery, analytical method equivalency, solvent consumption reduction, and laboratory ESG compliance.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="rounded-lg border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Key Highlights Included
                    </div>
                    <ul className="mt-2.5 space-y-2 text-sm text-foreground">
                      <li className="flex items-start gap-2">
                        <span className="mt-1 size-1.5 rounded-full bg-primary shrink-0" />
                        <span>Up to 90% recovery rates for isocratic HPLC solvents</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 size-1.5 rounded-full bg-primary shrink-0" />
                        <span>Uncompromised chromatographic baseline stability validation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 size-1.5 rounded-full bg-primary shrink-0" />
                        <span>Calculators for waste disposal and solvent expenditure ROI</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Link to="/HplcSustainabilityKnowledgeCentrePage" className="w-full">
                    <Button variant="default" className="w-full justify-between gap-2">
                      <span>Enter HPLC Sustainability Hub</span>
                      <ArrowRight className="size-4" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>

            {/* Knowledge Centre Grid */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {knowledgeHubs.map((hub, idx) => {
              const HubIcon = hub.icon;
              return <Card key={hub.title} data-index={idx} className="flex flex-col justify-between border border-border bg-card p-5 shadow-xs transition-all duration-200 hover:border-primary/40 hover:shadow-sm">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex size-9 items-center justify-center rounded-lg bg-secondary text-primary">
                          <HubIcon className="size-4.5" />
                        </div>
                        <Badge variant="outline" className="text-[11px]">
                          {hub.tag}
                        </Badge>
                      </div>
                      <h4 className="font-default text-base font-semibold text-foreground">
                        {hub.title}
                      </h4>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                        {hub.description}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-border">
                      <Link to={hub.href} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                        <span>Access Knowledge Centre</span>
                        <ChevronRight className="size-3.5" />
                      </Link>
                    </div>
                  </Card>;
            })}
            </div>
          </div>
        </div>

        {/* Live Articles & Publications Section */}
        <div className="mt-20">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
                <Clock className="size-3.5" />
                <span>Live Feed</span>
              </div>
              <h3 className="font-default text-2xl font-bold tracking-tight text-foreground">
                Latest Technical Articles & Publications
              </h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Updated automatically from the application knowledge base
            </p>
          </div>

          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date",
          status: ["publish"]
        }}>
            <LatestArticlesConsumer />
          </PostsProvider>
        </div>

        {/* Bottom Banner */}
        <div className="mt-16 rounded-xl border border-border bg-secondary/40 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center md:text-left">
            <h4 className="font-default text-lg font-semibold text-foreground">
              Require bespoke method validation or feasibility testing?
            </h4>
            <p className="mt-1 text-sm text-muted-foreground">
              Our application chemists provide structured feasibility studies and qualification lifecycle services for your exact instrumentation.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Link to="/FeasibilityStudiesPage">
              <Button variant="outline" size="sm">
                Feasibility Studies
              </Button>
            </Link>
            <Link to="/DiscussYourApplicationPage">
              <Button variant="default" size="sm" className="gap-1.5">
                Discuss Your Application
                <ArrowRight className="size-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}